/**
 * The signed-in person as the menu reads them: their role and tabs from the
 * session, plus whether their organisation has money to show (a stream that
 * sells, a return or a statement).
 *
 * Everything that decides what is on someone's menu (the rail, the command
 * bar, the CRM permission hook) takes its user from here, so they agree.
 *
 * The organisation's `features` switches are no longer read: they used to
 * hide whole areas, and now money appears where a stream sells and
 * everything else follows the login's own tabs and permissions. The setting
 * is still stored and served for apps already installed.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOptionalAuth } from '../auth/AuthProvider';
import { CAMPAIGNS_KEY, CAMPAIGNS_STALE, fetchCampaigns } from '../api/campaignList';
import type { GatewayUser } from '../api/auth';
import { isScopedUser, type MoneyPresence } from './nav';

export type MenuUser = (GatewayUser & { money: MoneyPresence | null }) | null;

export function useNavUser(): MenuUser {
  const user = useOptionalAuth()?.user ?? null;
  // The same cached list the filters use, so this costs nothing extra. Only
  // for the organisation's own staff: a buyer or supplier login's Money
  // items are its own statements and returns whatever the organisation
  // sells, and its browser never asks for the organisation's streams.
  const staff = !!user && !isScopedUser(user);
  const money = useQuery({
    queryKey: CAMPAIGNS_KEY,
    queryFn: fetchCampaigns,
    staleTime: CAMPAIGNS_STALE,
    enabled: staff,
  }).data?.money ?? null;
  return useMemo(() => (user ? { ...user, money } : null), [user, money]);
}
